import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import jsPDF from 'jspdf';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Document, Page } from 'react-pdf';
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useReactToPrint } from 'react-to-print';

import Button from '../../../components/Button/Button';
import { DownloadIcon, InfoIcon, PrintIcon, ShareIcon } from '../../../components/Icon/Icon';
import Popup from '../../../components/Popup/Popup';
import Tooltip from '../../../components/Tooltip/Tooltip';
import {
  CertificateWrapperStyled,
  ControlsWrapperStyled,
  MessageStyled,
  PopupContentStyle,
  PrintIconsWrapperStyled,
  SocialIconsWrapper,
  TitleStyled,
} from './CertificatePopup.styled';

type CertificatePropsT = {
  isOpen: boolean;
  certificateURL: string;
  socialTitle: string;
  popupTitle: string;
  onClose: () => void;
};

const CertificatePopup: React.FC<CertificatePropsT> = ({
  isOpen,
  certificateURL,
  socialTitle,
  popupTitle,
  onClose,
}) => {
  const [isCertificateLoaded, setCertificateLoaded] = useState(false);
  const [certificateWidth, setCertificateWidth] = useState(0);
  const certificateRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const setCertificateSize = () => {
    if (certificateRef && certificateRef.current) {
      setCertificateWidth(certificateRef.current?.getBoundingClientRect().width || 0);
    }
  };

  const handleDownloadPdf = () => {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
    });
    const dataURL = canvasRef.current?.toDataURL('image/png') || '';
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    pdf.addImage(dataURL, 'JPEG', 0, 0, width, height);
    pdf.save('certificate.pdf');

    return pdf;
  };

  const handlePrint = useReactToPrint({
    content: () => certificateRef.current,
  });

  useEffect(() => {
    setCertificateSize();
  }, [certificateRef.current]);

  return (
    <Popup isOpen={isOpen} onClose={onClose} contentStyle={PopupContentStyle}>
      <ControlsWrapperStyled>
        <TitleStyled level={4}>{popupTitle}</TitleStyled>
        <Tooltip interactive trigger={['click']} element={<ShareIcon />}>
          <SocialIconsWrapper>
            <TwitterShareButton
              disabled={!isCertificateLoaded}
              url={certificateURL}
              title={socialTitle}
            >
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <LinkedinShareButton disabled={!isCertificateLoaded} url={certificateURL}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <FacebookShareButton
              disabled={!isCertificateLoaded}
              url={certificateURL}
              quote={socialTitle}
            >
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <WhatsappShareButton
              disabled={!isCertificateLoaded}
              url={certificateURL}
              title={socialTitle}
              separator=":: "
            >
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
          </SocialIconsWrapper>
        </Tooltip>
        <Button modifier="inline" disabled={!isCertificateLoaded} onClick={handleDownloadPdf}>
          <DownloadIcon />
        </Button>

        <PrintIconsWrapperStyled>
          <Button modifier="inline" disabled={!isCertificateLoaded} onClick={handlePrint}>
            <PrintIcon />
          </Button>

          <Tooltip element={<InfoIcon />}>
            <FormattedMessage
              id="certificateDialog.tooltip.text"
              defaultMessage="Please consider the environment before printing."
            />
          </Tooltip>
        </PrintIconsWrapperStyled>
      </ControlsWrapperStyled>

      <CertificateWrapperStyled ref={certificateRef}>
        <Document
          file={{ url: certificateURL }}
          loading={
            <MessageStyled>
              <FormattedMessage
                id="certificateDialog.loading.text1"
                defaultMessage="Certificate is loading, please wait"
              />
            </MessageStyled>
          }
          error={
            <MessageStyled>
              <FormattedMessage
                id="certificateDialog.failed"
                defaultMessage="Failed to load PDF file."
              />
            </MessageStyled>
          }
          onSourceSuccess={() => setCertificateLoaded(true)}
        >
          <Page pageNumber={1} width={certificateWidth} canvasRef={canvasRef}></Page>
        </Document>
      </CertificateWrapperStyled>
    </Popup>
  );
};

export default CertificatePopup;
